/**
 * Kalkulator mocy instalacji PV — czysta logika, bez Reacta i bez backendu.
 *
 * PO CO: asystent doboru wymaga podania mocy w kWp, żeby cokolwiek policzyć
 * (patrz KitAssistant — bez `target_power_kwp` formularz się nie wyśle). Osoba,
 * która dopiero rozważa fotowoltaikę, tej liczby nie zna i w tym miejscu odpada.
 *
 * CZEGO TU NIE MA I NIE BĘDZIE: liczenia opłacalności, zwrotu z inwestycji ani
 * cen energii w przyszłości. Net-billing i ceny RCEm zmieniają się szybciej, niż
 * jesteśmy w stanie utrzymać, a obietnica zwrotu, która się nie sprawdzi, kosztuje
 * więcej zaufania, niż daje kliknięć. Liczymy MOC, nie pieniądze.
 *
 * Dokładność: ±15–20%. To szacunek do wypełnienia jednego pola formularza,
 * a nie projekt instalacji.
 */

import { KRAJ } from '../kraj'
import { CENA_ZAKUPU_ZL_KWH } from '../config/cenyEnergii'

// Cena energii brutto z dystrybucją — tutaj używana WYŁĄCZNIE do przeliczenia rachunku
// na kWh, gdy ktoś nie zna swojego zużycia. Trzymana jako stała nazwana i pokazana
// użytkownikowi wprost, bo to założenie, a nie fakt. Wartość (także rumuńska) mieszka
// w `config/cenyEnergii.js`, wspólnym dla kalkulatora, opłacalności i oferty.
export const CENA_ENERGII_ZL_KWH = CENA_ZAKUPU_ZL_KWH

// Uzysk z 1 kWp względem Polski. Policzone w PVGIS 5.3 (Komisja Europejska) dla
// 1 kWp, strat 14% i nachylenia 35°, 2026-10-08: średnia z 6 polskich miast to
// 1066 kWh na południe i 827 na zachód, z 8 rumuńskich 1255 i 983. Stosunek wychodzi
// 1,18 dla obu kierunków, więc mnożymy przez niego całą tabelę orientacji.
const WSPOLCZYNNIK_UZYSKU = { pl: 1, ro: 1.18 }
const W_UZYSKU = WSPOLCZYNNIK_UZYSKU[KRAJ.kod] ?? 1
const uzyskKraju = (kwh) => Math.round(kwh * W_UZYSKU)

// Uzysk z 1 kWp przy dachu na południe, do szybkich przeliczeń jednym pytaniem
// (wersja prosta asystenta).
export const UZYSK_KWH_NA_KWP_KRAJU = uzyskKraju(1000)

/**
 * Miejsce montażu. Instalacja NIE musi stać na dachu — na gruncie kierunek wybiera
 * się dowolnie, więc pytanie brzmi „gdzie skierujemy instalację", a nie „gdzie
 * skierowany jest dach".
 *
 * Świadomie BEZ premii za grunt. Kusi, żeby dodać kilka procent za optymalny kąt
 * i lepsze chłodzenie modułów, ale każda liczba, którą bym tu wpisał, byłaby
 * zgadywaniem — a wynik i tak ma dokładność ±15–20%.
 */
export const MONTAZE = [
  { key: 'dach', label: 'Na dachu' },
  { key: 'grunt', label: 'Na gruncie', opis: 'kierunek i kąt wybierasz sam' },
]

// Uzysk jednostkowy w kWh z 1 kWp rocznie, dla Polski, przy braku zacienienia
// (w innych krajach przemnożony przez WSPOLCZYNNIK_UZYSKU).
// `tylkoDach` odsiewa opcje, które na gruncie nie mają sensu: nikt nie stawia
// konstrukcji naziemnej skierowanej na północ ani „płaskiej".
export const ORIENTACJE = [
  { key: 'poludnie', label: 'Południe', opis: 'najwięcej energii', uzysk: uzyskKraju(1000) },
  { key: 'poludnie_bok', label: 'Płd.-wschód lub płd.-zachód', opis: 'ok. 6% mniej niż południe', uzysk: uzyskKraju(940) },
  // Dach płaski miał wcześniej 950 i to była wartość zawyżona: konstrukcja pod
  // 10–15° stoi wyraźnie dalej od kąta optymalnego (~35°) niż połać skośna.
  { key: 'plaski', label: 'Dach płaski', opis: 'konstrukcja pod 10–15°', uzysk: uzyskKraju(900), tylkoDach: true },
  { key: 'wschod_zachod', label: 'Wschód–zachód', opis: 'moduły w dwie strony', uzysk: uzyskKraju(830) },
  { key: 'jedna_polac', label: 'Tylko wschód albo zachód', opis: 'ok. jedna piąta mniej', uzysk: uzyskKraju(780) },
  // Zostawiamy wybieralne, ale z ostrzeżeniem. Ukrycie tej opcji sprawiłoby, że
  // ktoś z dachem północnym wybierze coś nieprawdziwego i dostanie zawyżoną moc.
  { key: 'polnoc', label: 'Północ', opis: 'odradzamy', uzysk: uzyskKraju(550), odradzane: true, tylkoDach: true },
]

export const orientacjeDla = (montaz) =>
  ORIENTACJE.filter((o) => montaz !== 'grunt' || !o.tylkoDach)

/**
 * Zacienienie w JEDNYM pytaniu.
 *
 * „Małe czy duże" to złe pytanie: mały cień leżący pół dnia zabiera więcej energii
 * niż duży, który przechodzi w kwadrans. Liczy się STRATA, a ta zależy głównie od
 * czasu — dlatego oś to długość zacienienia, a wielkość przeszkody siedzi
 * w przykładach przy każdej odpowiedzi. Jedno pytanie zamiast dwóch.
 */
/**
 * Współczynniki są NIŻSZE, niż wynikałoby z samej powierzchni cienia, i to nie jest
 * zapas bezpieczeństwa. Moduły w łańcuchu pracują szeregowo: zacienienie jednego
 * ściąga w dół cały łańcuch, więc cień na kilku procentach powierzchni potrafi
 * kosztować kilkanaście procent produkcji. Dlatego nawet „cień przechodzi na chwilę"
 * to realne 7%, a nie 2–3%, które sugerowałaby geometria.
 */
export const ZACIENIENIA = [
  { key: 'brak', label: 'Nic nie zasłania', opis: 'czysty widok na niebo', wsp: 1.0 },
  { key: 'chwilowe', label: 'Cień przechodzi na chwilę', opis: 'komin, maszt, słup', wsp: 0.93 },
  { key: 'kilka_godzin', label: 'Kilka godzin dziennie', opis: 'drzewo, sąsiedni budynek', wsp: 0.83 },
  { key: 'wiekszosc_dnia', label: 'Przez większość dnia', opis: 'las, wysoki budynek od południa', wsp: 0.7 },
]

/**
 * Profil zużycia.
 *
 * `mnoznik` to docelowe pokrycie rocznego zużycia produkcją, gdy NIE MA magazynu.
 * Przy net-billingu nadwyżkę sprzedaje się znacznie taniej, niż odkupuje wieczorem,
 * więc dom zużywający prąd po zmroku musi wyprodukować wyraźnie więcej, niż zużywa,
 * żeby wyjść na swoje. Stąd 125% i 140% zamiast okrągłego 100% — to mieści się
 * w tym, co pod net-billingiem proponują instalatorzy (typowo 120–140% przy
 * profilu domowym), i nie jest zapasem bezpieczeństwa dorzuconym „na wszelki wypadek".
 *
 * Przy zużyciu równomiernym zostaje 105%: nie ma niedopasowania do nadrobienia,
 * ale moduły z czasem tracą moc (ok. 0,5% rocznie), a część energii ginie po drodze.
 *
 * `pozaSloncem` to udział zużycia przypadający na godziny bez produkcji. Używamy go
 * dwa razy: do pojemności magazynu i do strat na jego ładowaniu (patrz obliczMoc).
 */
export const PROFILE = [
  {
    key: 'staly',
    label: 'Stale przez cały dzień',
    opis: 'praca z domu, firma, sklep',
    mnoznik: 1.05,
    autokonsumpcja: 0.4,
    pozaSloncem: 0.35,
  },
  {
    key: 'klasyczny',
    label: 'Rano i wieczorem',
    opis: 'najczęstszy w domach jednorodzinnych',
    mnoznik: 1.25,
    autokonsumpcja: 0.26,
    pozaSloncem: 0.5,
  },
  {
    key: 'wieczorny',
    label: 'Głównie wieczorem',
    opis: 'cały dzień poza domem',
    mnoznik: 1.4,
    autokonsumpcja: 0.2,
    pozaSloncem: 0.6,
  },
]

/**
 * Roczne zużycie wg liczby osób — ścieżka dla kogoś, kto nie zna ani zużycia,
 * ani rachunku.
 *
 * WAŻNE ZAŁOŻENIE: to skala dla DOMU JEDNORODZINNEGO, a nie średnia krajowa.
 * Wcześniejsze liczby (1800 kWh dla osoby samotnej) były bliższe średniej GUS,
 * a ta obejmuje głównie mieszkania — bez pompy obiegowej, garażu, ogrodu, warsztatu
 * i podgrzewania wody prądem. Kto porównuje oferty PV, mieszka w domu, więc baza
 * musi opisywać dom. Zawyżanie w drugą stronę też byłoby błędem, dlatego zostajemy
 * w górnej części typowych widełek, a nie powyżej nich.
 *
 * Krok na osobę to ok. 800 kWh — mniej więcej dwa dołożone moduły.
 */
export const ZUZYCIE_WG_OSOB = { 1: 2000, 2: 3000, 3: 3800, 4: 4600, 5: 5400, 6: 6200 }
export const MAX_OSOB = 6

/**
 * Katalog dodatkowych odbiorników. `kwh` to WARTOŚĆ WYJŚCIOWA — typowa, nie
 * jedyna słuszna. Kto zna zużycie swojej pompy ciepła, poprawia liczbę w polu obok;
 * pozycja „Inne" pozwala dopisać cokolwiek własnego wraz z nazwą.
 */
export const DODATKI = [
  { key: 'pompa', label: 'Pompa ciepła', kwh: 4500 },
  { key: 'auto', label: 'Samochód elektryczny', kwh: 2600 },
  { key: 'bojler', label: 'Bojler elektryczny', kwh: 1500 },
  { key: 'klimatyzacja', label: 'Klimatyzacja', kwh: 600 },
  { key: 'inne', label: 'Inne', kwh: 1000, wlasne: true },
]

/**
 * Moc pojedynczego modułu. To ONA wyznacza rozdzielczość całego wyniku: instalacji
 * nie stawia się z ułamka modułu, więc każda możliwa odpowiedź kalkulatora jest
 * wielokrotnością tej liczby.
 *
 * Wcześniej wynik zaokrąglaliśmy do 0,5 kWp i to był błąd. Krok 0,5 kWp nie
 * odpowiada niczemu w rzeczywistości, a przy instalacji 4 kWp to aż 12% — więc
 * zjadał całą różnicę między odpowiedziami. Dom na południe bez cienia (3,80 kWp)
 * i dom na płd.-zachód z cieniem (4,21 kWp) wychodziły identycznie, mimo 11%
 * różnicy w zapotrzebowaniu. Pytania o kierunek i zacienienie nie miały wtedy
 * wpływu na nic. Zmierzone.
 */
/**
 * Sprawność magazynu w cyklu ładowanie–rozładowanie.
 *
 * GDZIE TO DZIAŁA: obniża AUTOKONSUMPCJĘ, a nie podnosi wymaganej mocy. Przez chwilę
 * doliczaliśmy te straty do liczby modułów i to było liczenie podwójne — bez magazynu
 * nadwyżki nie tracisz, tylko oddajesz ją do sieci i odkupujesz. Tracisz na tym
 * pieniądze, nie kilowatogodziny. Straty baterii zmniejszają to, ile z własnej
 * produkcji realnie zużyjesz, i tam mają być widoczne.
 *
 * Wyeksportowana, bo projekt dachu 3D pokazuje ją jako założenie, gdy karta magazynu
 * nie podaje własnej sprawności (patrz `samowystarczalnoscZOkres`).
 */
export const SPRAWNOSC_MAGAZYNU = 0.9

/**
 * Ile z niedopasowania produkcji do zużycia zostaje MIMO magazynu.
 *
 * Nie zero, bo magazyn dobiera się pod typowy dzień, a nie pod grudzień — zimą, przy
 * kilkukrotnie niższej produkcji, bateria i tak się nie napełni i dom zużywający prąd
 * po zmroku znowu kupuje z sieci. Ale też niewiele: przez większą część roku bateria
 * robi dokładnie to, po co jest, więc instalację z magazynem wymiarujemy w praktyce
 * na roczną równowagę produkcji ze zużyciem.
 */
const RESZTA_NIEDOPASOWANIA_Z_MAGAZYNEM = 0.15

const MOC_MODULU_KW = 0.45
const POWIERZCHNIA_MODULU_M2 = 2
// Poniżej czterech modułów nie ma o czym mówić, powyżej 50 kWp to nie mikroinstalacja.
const MIN_MODULOW = 4
const MAX_MODULOW = Math.floor(50 / MOC_MODULU_KW)
const BONUS_AUTOKONSUMPCJI_Z_MAGAZYNEM = 0.32
const SUFIT_AUTOKONSUMPCJI = 0.8
// Widełki zdrowego rozsądku: magazyn poniżej 0,8 kWh na kWp nie zdąży się naładować,
// powyżej 1,5 kWh na kWp przez większość roku stoi pusty.
const MAGAZYN_MIN_NA_KWP = 0.8
const MAGAZYN_MAX_NA_KWP = 1.5

// Pojemność magazynu — TU krok 0,5 kWh jest zasadny: moduły bateryjne
// faktycznie sprzedaje się w takich stopniach.
const doPolowek = (v) => Math.round(v * 2) / 2
const liczba = (v) => {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(',', '.'))
  return Number.isFinite(n) && n > 0 ? n : 0
}

/**
 * Dodatki przyjmujemy w dwóch postaciach: samych kluczy (`['pompa']` — wtedy liczy
 * się wartość katalogowa) albo obiektów z własnym zużyciem (`[{key:'pompa', kwh:5200}]`).
 * Dzięki temu wywołanie bez korekty pozostaje krótkie, a interfejs może podać
 * poprawioną liczbę bez osobnego pola w API.
 */
export const normalizujDodatki = (dodatki = []) => dodatki.map((d) => {
  const klucz = typeof d === 'string' ? d : d.key
  const wzor = DODATKI.find((x) => x.key === klucz)
  const wlasne = typeof d === 'string' ? 0 : liczba(d.kwh)
  return {
    key: klucz,
    kwh: wlasne || (wzor ? wzor.kwh : 0),
    label: (typeof d === 'object' && d.label) || (wzor ? wzor.label : klucz),
    poprawiony: Boolean(wlasne) && wzor ? wlasne !== wzor.kwh : false,
  }
})

/**
 * Roczne zużycie w kWh z jednej z trzech dróg wejścia.
 *
 * Dodatki doliczamy w KAŻDEJ drodze, bo pytamy o sprzęt, którego jeszcze nie ma
 * („planuję pompę ciepła"). W interfejsie stoi przy tym ostrzeżenie, żeby nie
 * zaznaczać tego, co już siedzi w podanym zużyciu — inaczej policzy się dwa razy.
 */
export const zuzycieRoczne = ({
  sposob, kwh, rachunek, osoby, dodatki = [], cenaKwh = CENA_ENERGII_ZL_KWH,
}) => {
  // `cenaKwh`: cena zakupu klienta, gdy ją znamy (karta klienta); inaczej założenie wyżej.
  const cena = liczba(cenaKwh) || CENA_ENERGII_ZL_KWH
  let baza = 0
  if (sposob === 'kwh') baza = liczba(kwh)
  else if (sposob === 'rachunek') baza = (liczba(rachunek) * 12) / cena
  else baza = ZUZYCIE_WG_OSOB[Math.min(MAX_OSOB, Math.max(1, Math.round(liczba(osoby) || 3)))]

  const dodatkowe = normalizujDodatki(dodatki).reduce((s, d) => s + d.kwh, 0)

  return Math.round(baza + dodatkowe)
}

/**
 * Główne wyliczenie.
 *
 * moc = zużycie / (uzysk × zacienienie) × mnożnik profilu
 *
 * Przy magazynie mnożnik profilu wynosi 1,0 niezależnie od odpowiedzi: magazyn
 * przesuwa produkcję na wieczór, więc nie ma po co dokładać modułów po to, żeby
 * nadwyżkę i tak oddać do sieci.
 */
export const obliczMoc = ({
  sposob = 'osoby', kwh, rachunek, osoby = 3, dodatki = [],
  montaz = 'dach', orientacja = 'poludnie', zacienienie = 'brak',
  profil = 'klasyczny', magazyn = false,
}) => {
  // Na gruncie „dach płaski" i „północ" nie istnieją — gdyby taki wybór został
  // po przełączeniu montażu, cichaczem policzyłby zaniżony uzysk. Wracamy na południe.
  const dostepne = orientacjeDla(montaz)
  const o = dostepne.find((x) => x.key === orientacja) || dostepne[0]
  const z = ZACIENIENIA.find((x) => x.key === zacienienie) || ZACIENIENIA[0]
  const p = PROFILE.find((x) => x.key === profil) || PROFILE[1]

  const zuzycie = zuzycieRoczne({ sposob, kwh, rachunek, osoby, dodatki })
  const uzysk = o.uzysk * z.wsp

  /**
   * Wpływ profilu zużycia na moc — także PRZY magazynie.
   *
   * Wcześniej magazyn ustawiał mnożnik na twarde 1,0, przez co pytanie „kiedy
   * zużywasz prąd" nie zmieniało wtedy niczego. A na stronie głównej wariant
   * „Zastanawiam się" włącza magazyn domyślnie, więc dla części użytkowników krok 4
   * był dekoracją. Zmierzone.
   *
   * Magazyn NIGDY nie podnosi wymaganej mocy — na tym polega jego sens: energię
   * z południa oddaje wieczorem, więc nie trzeba nadrabiać modułami tego, co i tak
   * poszłoby do sieci. Zmniejsza nadwymiarowanie tam, gdzie było co zmniejszać
   * (zużycie wieczorne), a przy zużyciu równomiernym po prostu nic nie zmienia,
   * bo nadwymiarowania tam nie ma.
   *
   * Zostaje POŁOWA niedopasowania, nie całość: baterię dobiera się pod typowy dzień,
   * a nie pod grudzień — zimą, przy kilkukrotnie niższej produkcji, i tak się nie
   * napełni, a dom zużywający prąd po zmroku znowu kupuje z sieci.
   */
  const mnoznik = magazyn
    ? 1 + (p.mnoznik - 1) * RESZTA_NIEDOPASOWANIA_Z_MAGAZYNEM
    : p.mnoznik

  // Zapotrzebowanie PRZED zaokrągleniem — pokazujemy je obok wyniku, żeby było
  // widać wpływ odpowiedzi także wtedy, gdy nie przeskakuje jeszcze o cały moduł.
  const mocSurowaKwp = Math.round((zuzycie / uzysk) * mnoznik * 100) / 100

  const liczbaModulow = Math.min(
    MAX_MODULOW,
    Math.max(MIN_MODULOW, Math.round(mocSurowaKwp / MOC_MODULU_KW))
  )
  const mocKwp = Math.round(liczbaModulow * MOC_MODULU_KW * 100) / 100

  const produkcjaKwh = Math.round(mocKwp * uzysk)
  // Energia przełożona przez baterię wraca uszczuplona o straty cyklu, więc bonus
  // liczymy po sprawności — inaczej obiecywalibyśmy autokonsumpcję, której magazyn
  // fizycznie nie dowozi.
  const autokonsumpcja = magazyn
    ? Math.min(
      SUFIT_AUTOKONSUMPCJI,
      p.autokonsumpcja + BONUS_AUTOKONSUMPCJI_Z_MAGAZYNEM * SPRAWNOSC_MAGAZYNU
    )
    : p.autokonsumpcja

  let pojemnoscMagazynuKwh = null
  if (magazyn) {
    const surowa = (zuzycie / 365) * p.pozaSloncem
    pojemnoscMagazynuKwh = doPolowek(
      Math.min(mocKwp * MAGAZYN_MAX_NA_KWP, Math.max(mocKwp * MAGAZYN_MIN_NA_KWP, surowa))
    )
  }

  const ostrzezenia = []
  if (o.odradzane) {
    ostrzezenia.push('Dach północny daje o ok. 40% mniej energii. Zanim zdecydujesz, sprawdź inną połać albo montaż na gruncie.')
  }
  if (z.key === 'wiekszosc_dnia') {
    ostrzezenia.push('Przy zacienieniu przez większość dnia wynik jest tylko orientacyjny — potrzebna analiza zacienienia na miejscu.')
  }
  if (mocKwp >= 10) {
    ostrzezenia.push('Powyżej 10 kWp potrzebny jest falownik trójfazowy, a warunki przyłączenia bywają droższe. Sprawdź je u operatora przed zakupem.')
  }
  // Warunek na zapotrzebowaniu SUROWYM, nie na wyniku: wynik jest przycięty do
  // MAX_MODULOW (49,95 kWp), więc `mocKwp >= 50` nie mogłoby się nigdy spełnić
  // i ostrzeżenie o mikroinstalacji przepadłoby po cichu.
  if (mocSurowaKwp > 50) {
    ostrzezenia.push('Twoje zapotrzebowanie przekracza 50 kWp, czyli granicę mikroinstalacji — obowiązują wtedy inne zasady rozliczenia i przyłączenia.')
  }

  return {
    zuzycie,
    orientacja: o.key,
    uzyskJednostkowy: Math.round(uzysk),
    mocKwp,
    produkcjaKwh,
    pokrycie: zuzycie > 0 ? Math.round((produkcjaKwh / zuzycie) * 100) : 0,
    mocSurowaKwp,
    liczbaModulow,
    mocModuluW: Math.round(MOC_MODULU_KW * 1000),
    powierzchniaM2: liczbaModulow * POWIERZCHNIA_MODULU_M2,
    autokonsumpcjaProc: Math.round(autokonsumpcja * 100),
    autokonsumpcjaKwh: Math.round(produkcjaKwh * autokonsumpcja),
    pojemnoscMagazynuKwh,
    ostrzezenia,
  }
}

/* ═══════════════════════════════════════════════════════════════════════════
   PORÓWNANIE WARIANTÓW: z magazynem vs bez magazynu
   ═══════════════════════════════════════════════════════════════════════════

   Prawdziwe pytanie brzmi nie „ile kWp", tylko „czy magazyn jest potrzebny".
   Żeby na nie odpowiedzieć, trzeba umieć policzyć SAMOWYSTARCZALNOŚĆ — jaką część
   swojego zużycia pokryjesz własną produkcją — a nie samą roczną sumę kilowatogodzin.

   Kluczowa rzecz, której nie widać w prostym bilansie rocznym: bez magazynu
   samowystarczalność NASYCA SIĘ. Dokładanie modułów podnosi ją coraz słabiej, bo
   w południe i tak produkujesz więcej, niż jesteś w stanie zużyć. Istnieje sufit,
   którego nie przeskoczy żadna liczba paneli: energii zużywanej po zmroku nie da
   się pokryć produkcją, której wtedy nie ma. Ten sufit to po prostu udział zużycia
   przypadający na godziny z produkcją.
   ═══════════════════════════════════════════════════════════════════════════ */

// Sufit samowystarczalności bez magazynu — udział zużycia w godzinach produkcji.
const sufitBezMagazynu = (p) => 1 - p.pozaSloncem

/**
 * Stała krzywej nasycenia, dobrana tak, żeby przy produkcji równej zużyciu (r = 1)
 * wyjść dokładnie na autokonsumpcję znaną z profilu. Dzięki temu model rozszerza
 * liczby, które już mamy, zamiast wprowadzać drugi, niezgodny z nimi zestaw.
 */
const stalaNasycenia = (p) => -Math.log(1 - p.autokonsumpcja / sufitBezMagazynu(p))

/** Samowystarczalność bez magazynu przy stosunku produkcji do zużycia `r`. */
export const samowystarczalnoscBez = (p, r) =>
  sufitBezMagazynu(p) * (1 - Math.exp(-stalaNasycenia(p) * r))

/**
 * Ile dni w roku magazyn realnie odda pełny cykl. Nie 365: zimą się nie napełnia,
 * a w słoneczne dni bywa pełny, zanim skończy się produkcja.
 */
const WYKORZYSTANIE_MAGAZYNU = 0.75

/**
 * Samowystarczalność z magazynem. Bateria przekłada energię z południa na wieczór,
 * ale nie więcej, niż pozwala NAJSŁABSZE z trzech ograniczeń: dostępna nadwyżka,
 * realne zapotrzebowanie nocne i jej własna przepustowość roczna.
 *
 * `sprawnosc` (0–1) to sprawność cyklu konkretnego magazynu, gdy znamy ją z karty
 * katalogowej (projekt dachu 3D). Bez niej obowiązuje SPRAWNOSC_MAGAZYNU, więc
 * dotychczasowe wywołania liczą dokładnie to samo co wcześniej.
 */
export const samowystarczalnoscZOkres = (
  p, r, pojemnoscKwh, zuzycieOkresu, dni, bazowa = null, sprawnosc = SPRAWNOSC_MAGAZYNU,
) => {
  const bez = bazowa != null ? bazowa : samowystarczalnoscBez(p, r)
  const nadwyzka = zuzycieOkresu * r - zuzycieOkresu * bez
  const potrzebaNocna = zuzycieOkresu * p.pozaSloncem
  const przepustowosc = dni * pojemnoscKwh * WYKORZYSTANIE_MAGAZYNU
  const przelozone = Math.max(0, Math.min(nadwyzka, potrzebaNocna, przepustowosc)) * sprawnosc
  return Math.min(1, bez + przelozone / zuzycieOkresu)
}

/**
 * Wersja roczna. Prognoza miesięczna (patrz lib/prognozaProdukcji) potrzebuje tej
 * samej logiki dla 28–31 dni, a dwa osobne wzory na to samo rozjechałyby się przy
 * pierwszej poprawce — dlatego okres jest parametrem, a rok tylko jego przypadkiem.
 */
export const samowystarczalnoscZ = (p, r, pojemnoscKwh, zuzycie) =>
  samowystarczalnoscZOkres(p, r, pojemnoscKwh, zuzycie, 365)


/**
 * Trzy wielkości magazynu do porównania: mniejszy, dopasowany i większy.
 *
 * PO CO: „6,5 kWh" nic nikomu nie mówi, dopóki nie widać, co dałaby bateria mniejsza
 * albo większa. Dopiero zestawienie pokazuje, gdzie leży punkt, w którym dokładanie
 * pojemności przestaje się opłacać — bo tu też jest nasycenie: magazyn większy niż
 * nocne zapotrzebowanie stoi pusty niezależnie od tego, ile ma kilowatogodzin.
 *
 * Wielkości liczone OD REKOMENDACJI, a nie wpisane na sztywno: przy domu z pompą
 * ciepła „mały" magazyn to co innego niż przy mieszkaniu dwóch osób.
 */
export const WIELKOSCI_MAGAZYNU = [
  { key: 'maly', label: 'Mniejszy', mnoznik: 0.6 },
  { key: 'dopasowany', label: 'Dopasowany', mnoznik: 1.0, polecany: true },
  { key: 'duzy', label: 'Większy', mnoznik: 1.6 },
]

export const wariantyMagazynu = (wejscie) => {
  const w = obliczMoc({ ...wejscie, magazyn: true })
  const p = PROFILE.find((x) => x.key === (wejscie.profil || 'klasyczny')) || PROFILE[1]
  const r = w.produkcjaKwh / w.zuzycie
  const bazowa = w.pojemnoscMagazynuKwh || 1

  return WIELKOSCI_MAGAZYNU.map((wielkosc) => {
    const pojemnosc = Math.max(1, doPolowek(bazowa * wielkosc.mnoznik))
    return {
      ...wielkosc,
      pojemnoscKwh: pojemnosc,
      samowystarczalnoscProc: Math.round(samowystarczalnoscZ(p, r, pojemnosc, w.zuzycie) * 100),
    }
  })
}

/** Pojemność dla wybranej wielkości — używane i w oknie, i przy przekazaniu do asystenta. */
export const pojemnoscDla = (wejscie, klucz) => {
  const warianty = wariantyMagazynu(wejscie)
  const wybrany = warianty.find((x) => x.key === klucz) || warianty.find((x) => x.polecany)
  return wybrany.pojemnoscKwh
}

/** Jakie `r` daje zadaną samowystarczalność bez magazynu. `null` = nieosiągalne. */
const rDlaCelu = (p, cel) => {
  const sufit = sufitBezMagazynu(p)
  if (cel >= sufit) return null
  return -Math.log(1 - cel / sufit) / stalaNasycenia(p)
}

const naModuly = (kwp) => Math.min(MAX_MODULOW, Math.max(MIN_MODULOW, Math.round(kwp / MOC_MODULU_KW)))
const zModulow = (n) => Math.round(n * MOC_MODULU_KW * 100) / 100

/**
 * Zestawienie obu wariantów dla tego samego domu.
 *
 * Zwraca wariant z magazynem, wariant bez magazynu przy tej samej mocy (żeby było
 * widać, co się traci) oraz odpowiedź na pytanie „ile paneli musiałbym postawić,
 * żeby bez magazynu wyjść na to samo" — razem z uczciwym „nie da się", gdy cel leży
 * powyżej sufitu.
 */
export const porownajWarianty = (wejscie) => {
  const zMag = obliczMoc({ ...wejscie, magazyn: true })
  const bezMag = obliczMoc({ ...wejscie, magazyn: false })
  const p = PROFILE.find((x) => x.key === (wejscie.profil || 'klasyczny')) || PROFILE[1]
  const zuzycie = zMag.zuzycie
  const uzysk = zMag.uzyskJednostkowy

  // Pojemność WYBRANA przez użytkownika ma pierwszeństwo nad rekomendowaną —
  // inaczej karta pokazywałaby pokrycie dla baterii, której nikt nie wybrał.
  const pojemnosc = wejscie.pojemnoscKwh > 0 ? wejscie.pojemnoscKwh : zMag.pojemnoscMagazynuKwh
  const samZ = samowystarczalnoscZ(p, zMag.produkcjaKwh / zuzycie, pojemnosc, zuzycie)
  const samBez = samowystarczalnoscBez(p, bezMag.produkcjaKwh / zuzycie)

  // Ile modułów trzeba by BEZ magazynu, żeby wyjść na to samo pokrycie.
  const sufit = sufitBezMagazynu(p)
  const rCel = rDlaCelu(p, samZ)
  let dorownanie
  if (rCel === null) {
    const przyMaksimum = samowystarczalnoscBez(p, (MAX_MODULOW * MOC_MODULU_KW * uzysk) / zuzycie)
    dorownanie = {
      osiagalne: false,
      sufitProc: Math.round(sufit * 100),
      przyMaksimumProc: Math.round(przyMaksimum * 100),
    }
  } else {
    const moduly = naModuly((rCel * zuzycie) / uzysk)
    dorownanie = {
      osiagalne: true,
      liczbaModulow: moduly,
      mocKwp: zModulow(moduly),
      krotnosc: Math.round((moduly / bezMag.liczbaModulow) * 10) / 10,
      sufitProc: Math.round(sufit * 100),
    }
  }

  return {
    zuzycie,
    zMagazynem: {
      liczbaModulow: zMag.liczbaModulow,
      mocKwp: zMag.mocKwp,
      produkcjaKwh: zMag.produkcjaKwh,
      powierzchniaM2: zMag.powierzchniaM2,
      pojemnoscKwh: pojemnosc,
      samowystarczalnoscProc: Math.round(samZ * 100),
    },
    bezMagazynu: {
      liczbaModulow: bezMag.liczbaModulow,
      mocKwp: bezMag.mocKwp,
      produkcjaKwh: bezMag.produkcjaKwh,
      powierzchniaM2: bezMag.powierzchniaM2,
      samowystarczalnoscProc: Math.round(samBez * 100),
      sufitProc: Math.round(sufit * 100),
    },
    dorownanie,
    ostrzezenia: zMag.ostrzezenia,
  }
}
